/* Active navigation: what the server has computed about the leg being sailed.
 *
 * The arithmetic is the server's — `navigation.course.calcValues.*`, published
 * by the course-provider — so this file is about PRESENTING it without changing
 * what it means. That sounds like a small job right up until the cross-track
 * error, which is the one number here that tells the helm which way to turn.
 *
 * ═══ THE XTE SIGN, MEASURED RATHER THAN ASSUMED ═══
 *
 * A cross-track error displayed with the wrong side is worse than no cross-track
 * error at all: it is a confident instruction to steer further off the track,
 * and it stays plausible all the way onto the rocks. So the convention was
 * checked twice, against the boat rather than against a guess.
 *
 * SignalK's own meta for the path says it outright:
 *
 *   "A negative number indicates that the vessel is currently to the left of
 *    this line (and thus must steer right to compensate), a positive number
 *    means the vessel is to the right of the line (steer left to compensate)."
 *
 * And the live boat agreed, on 3 Aug 2026 with a course running: the vessel was
 * 1020 m to STARBOARD of the previousPoint→nextPoint line by direct calculation
 * from the three positions, and the server reported crossTrackError = +1027 m.
 *
 * So: POSITIVE = the boat is right of track = STEER PORT.
 *
 * Note that freeboard-sk was no help here — it prints the signed number and
 * never says a side, so it could not confirm or deny the convention.
 */

/** Which side of the track the boat is on, and which way to steer back. */
export interface XteView {
  /** Distance off track, always positive — the side is carried separately. */
  metres: number;
  /** Where the BOAT is, relative to the track. */
  boatSide: 'PORT' | 'STBD';
  /** Which way to turn to regain it. The opposite side, and the useful one. */
  steer: 'PORT' | 'STBD';
  /** −1..1 for a bar, clamped. Negative draws left of centre. */
  frac: number;
}

/**
 * @param m      signed crossTrackError in metres, server convention
 * @param fullM  the offset that pins the bar to one end
 */
export function xteView(m: number | null | undefined, fullM: number): XteView | null {
  if (typeof m !== 'number' || !Number.isFinite(m)) return null;
  const boatSide = m >= 0 ? 'STBD' : 'PORT';
  /* The bar draws where the BOAT is, so a boat right of track sits right of
   * centre and the track is what you steer back towards. Drawing the steer
   * direction instead would put the marker on the opposite side from the
   * vessel, which is the same information rendered as a lie. */
  const frac = fullM > 0 ? Math.max(-1, Math.min(1, m / fullM)) : 0;
  return {
    metres: Math.abs(m),
    boatSide,
    steer: boatSide === 'STBD' ? 'PORT' : 'STBD',
    frac
  };
}

/** The arrow a helm reads without reading: points the way to turn. */
export const steerArrow = (v: XteView | null): string =>
  v === null ? '' : v.steer === 'PORT' ? '◀' : '▶';

/* Below about a tenth of a mile the interesting digit is the third one, and
 * above it nobody cares about metres. Same split freeboard uses. */
export function fmtXte(v: XteView | null): string {
  if (!v) return '—';
  const nm = v.metres / 1852;
  return nm >= 0.1 ? nm.toFixed(2) : nm.toFixed(3);
}

/** Seconds to go → "26m", "1h26", "—". Never "0h26" and never a bare 1560. */
export function fmtEte(s: number | null | undefined): string {
  if (typeof s !== 'number' || !Number.isFinite(s) || s < 0) return '—';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  /* Past a day the hours stop meaning anything to somebody planning a passage,
   * and "37h" reads better than "1d13h" on a strip this size. */
  return `${h}h${String(mins % 60).padStart(2, '0')}`;
}

/**
 * ISO arrival time → clock time, with a marker when it is not today.
 * A bare "04:15" for an arrival two days out is the kind of readout somebody
 * plans a night passage around.
 */
export function fmtEta(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return '—';
  const hhmm = `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
  const days = Math.round(
    (new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime() -
      new Date(new Date(now).getFullYear(), new Date(now).getMonth(), new Date(now).getDate()).getTime()) /
      86_400_000
  );
  if (days === 0) return hhmm;
  if (days === 1) return `${hhmm}+1`;
  return `${hhmm}+${days}`;
}

/** Metres → nautical miles at the resolution a plotter shows. */
export const fmtNm = (m: number | null | undefined): string =>
  typeof m === 'number' && Number.isFinite(m) ? (m / 1852).toFixed(2) : '—';

/** Radians true → a three-digit compass string. */
export const fmtBrg = (rad: number | null | undefined): string =>
  typeof rad === 'number' && Number.isFinite(rad)
    ? String(Math.round((((rad * 180) / Math.PI) % 360 + 360) % 360)).padStart(3, '0') + '°'
    : '—';

/* VMG toward the destination. NEGATIVE IS REAL AND MUST SURVIVE: it means the
 * leg is getting longer, which is what beating away from a mark looks like and
 * exactly when somebody wants to see it. Math.abs here would hide a tack that
 * is losing ground. */
export const fmtVmg = (ms: number | null | undefined): string =>
  typeof ms === 'number' && Number.isFinite(ms) ? (ms * 1.94384).toFixed(1) : '—';
